import React from 'react'
import { View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Icon, Text } from '@components'
import { getTierCardStyles } from './tierCardStyle'

export type TierCardProps = {
  /** Tier name (e.g. "Version Gratuite", "Keeptouch Infinity"). */
  title: string
  /** One-line positioning under the title. */
  subtitle: string
  /** Already-formatted price label (e.g. "Gratuit", "6,99 €/mois"). */
  price: string
  /** Already-translated feature lines to list. */
  features: string[]
  /** The recommended tier — gold accent, badge, soft lift. */
  highlighted?: boolean
  /** Optional pill shown on the highlighted tier. */
  badgeLabel?: string
  /** Slot for the tier's CTA (only the highlighted tier has one). */
  children?: React.ReactNode
  /** testID for the card root. */
  testID?: string
}

export const TierCard = ({
  title,
  subtitle,
  price,
  features,
  highlighted = false,
  badgeLabel,
  children,
  testID,
}: TierCardProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getTierCardStyles(theme, highlighted)

  return (
    <View style={styles.card} testID={testID}>
      {highlighted && badgeLabel ? (
        <View style={styles.badge}>
          <Icon name="star" size={theme.iconSize.extraSmall} color={theme.colors.white} />
          <Text type="bodyExtraSmallBold" color={theme.colors.white}>
            {badgeLabel}
          </Text>
        </View>
      ) : null}

      <Text type="subtitleBold" style={styles.title}>
        {title}
      </Text>
      <Text type="bodySmall" style={styles.subtitle}>
        {subtitle}
      </Text>

      <Text
        type="titleBigBold"
        style={styles.price}
        color={highlighted ? theme.colors.gold : theme.colors.black}
      >
        {price}
      </Text>

      <View style={styles.divider} />

      <View style={styles.features}>
        {features.map((feature) => (
          <View key={feature} style={styles.featureRow}>
            <View style={styles.featureMarker}>
              {highlighted ? (
                <Icon name="check" size={theme.iconSize.small} color={theme.colors.gold} />
              ) : (
                <View style={styles.featureDot} />
              )}
            </View>
            <Text type="bodySmall" style={styles.featureText}>
              {feature}
            </Text>
          </View>
        ))}
      </View>

      {children ? <View style={styles.ctaSlot}>{children}</View> : null}
    </View>
  )
}
